import type { IAwsRecord } from '../shared/storable';
import { TypeNames } from '../shared/storable';

export type VaultJsonValue = string | number | boolean | null | VaultJsonValue[] | { [key: string]: VaultJsonValue };

export type VaultJsonObject = { [key: string]: VaultJsonValue };

export const VaultRecordType = {
  Document: 'DOCUMENT',
  DocumentVersion: 'DOCUMENT_VERSION',
  DocumentLink: 'DOCUMENT_LINK',
  DocumentAuditEvent: 'DOCUMENT_AUDIT_EVENT',
} as const;

export type VaultRecordType = (typeof VaultRecordType)[keyof typeof VaultRecordType];

export const DocumentType = {
  File: 'FILE',
  GeneratedPdf: 'GENERATED_PDF',
  SignedPdf: 'SIGNED_PDF',
  Image: 'IMAGE',
  Video: 'VIDEO',
  ExternalLink: 'EXTERNAL_LINK',
} as const;

export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export const DocumentCategory = {
  OwnershipLegal: 'OWNERSHIP_LEGAL',
  Warranty: 'WARRANTY',
  Permit: 'PERMIT',
  Inspection: 'INSPECTION',
  Invoice: 'INVOICE',
  Manual: 'MANUAL',
  Contract: 'CONTRACT',
  Insurance: 'INSURANCE',
  Maintenance: 'MAINTENANCE',
  Project: 'PROJECT',
  Utility: 'UTILITY',
  PhotoEvidence: 'PHOTO_EVIDENCE',
  Financial: 'FINANCIAL',
  Tax: 'TAX',
  Safety: 'SAFETY',
  System: 'SYSTEM',
  Other: 'OTHER',
} as const;

export type DocumentCategory = (typeof DocumentCategory)[keyof typeof DocumentCategory];

export const DocumentStatus = {
  Active: 'ACTIVE',
  Archived: 'ARCHIVED',
  Superseded: 'SUPERSEDED',
  Draft: 'DRAFT',
  PendingReview: 'PENDING_REVIEW',
} as const;

export type DocumentStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];

export const DocumentOwnerType = {
  User: 'USER',
  Business: 'BUSINESS',
  System: 'SYSTEM',
} as const;

export type DocumentOwnerType = (typeof DocumentOwnerType)[keyof typeof DocumentOwnerType];

export const DocumentVisibilityScope = {
  Private: 'PRIVATE',
  PropertyShared: 'PROPERTY_SHARED',
  ProjectShared: 'PROJECT_SHARED',
  OrgInternal: 'ORG_INTERNAL',
  LegalHold: 'LEGAL_HOLD',
} as const;

export type DocumentVisibilityScope = (typeof DocumentVisibilityScope)[keyof typeof DocumentVisibilityScope];

export const DocumentVersionUploadStatus = {
  Pending: 'PENDING',
  Completed: 'COMPLETED',
  Failed: 'FAILED',
} as const;

export type DocumentVersionUploadStatus =
  (typeof DocumentVersionUploadStatus)[keyof typeof DocumentVersionUploadStatus];

export const DocumentVersionVirusScanStatus = {
  Pending: 'PENDING',
  Clean: 'CLEAN',
  Quarantined: 'QUARANTINED',
  Failed: 'FAILED',
} as const;

export type DocumentVersionVirusScanStatus =
  (typeof DocumentVersionVirusScanStatus)[keyof typeof DocumentVersionVirusScanStatus];

export const DocumentProcessingStatus = {
  Pending: 'PENDING',
  Done: 'DONE',
  Failed: 'FAILED',
} as const;

export type DocumentProcessingStatus = (typeof DocumentProcessingStatus)[keyof typeof DocumentProcessingStatus];

export const DocumentLinkedEntityType = {
  Property: 'PROPERTY',
  Project: 'PROJECT',
  System: 'SYSTEM',
  Utility: 'UTILITY',
  Location: 'LOCATION',
  Checklist: 'CHECKLIST',
  ChecklistItem: 'CHECKLIST_ITEM',
  Claim: 'CLAIM',
  TransferPackage: 'TRANSFER_PACKAGE',
} as const;

export type DocumentLinkedEntityType = (typeof DocumentLinkedEntityType)[keyof typeof DocumentLinkedEntityType];

export const DocumentRelationType = {
  Primary: 'PRIMARY',
  Supporting: 'SUPPORTING',
  Evidence: 'EVIDENCE',
  HandoverRequired: 'HANDOVER_REQUIRED',
  HandoverIncluded: 'HANDOVER_INCLUDED',
  AcceptanceAttachment: 'ACCEPTANCE_ATTACHMENT',
  WarrantyProof: 'WARRANTY_PROOF',
  LegalEvidence: 'LEGAL_EVIDENCE',
} as const;

export type DocumentRelationType = (typeof DocumentRelationType)[keyof typeof DocumentRelationType];

export const DocumentAuditAction = {
  Created: 'CREATED',
  Uploaded: 'UPLOADED',
  Viewed: 'VIEWED',
  Downloaded: 'DOWNLOADED',
  Shared: 'SHARED',
  Signed: 'SIGNED',
  Acknowledged: 'ACKNOWLEDGED',
  VersionUploaded: 'VERSION_UPLOADED',
  Classified: 'CLASSIFIED',
  MetadataUpdated: 'METADATA_UPDATED',
  Linked: 'LINKED',
  Unlinked: 'UNLINKED',
  Archived: 'ARCHIVED',
  Restored: 'RESTORED',
  LegalHoldApplied: 'LEGAL_HOLD_APPLIED',
} as const;

export type DocumentAuditAction = (typeof DocumentAuditAction)[keyof typeof DocumentAuditAction];

export const VaultAuditActorType = {
  User: 'USER',
  System: 'SYSTEM',
} as const;

export type VaultAuditActorType = (typeof VaultAuditActorType)[keyof typeof VaultAuditActorType];

export type DocumentSubcategory = string;

export interface PropertyDocument extends IAwsRecord {
  typeName: typeof TypeNames.Document;
  vaultRecordType: typeof VaultRecordType.Document;
  documentID: string;
  propertyID: string;
  orgID?: string | null;
  projectID?: string | null;
  title: string;
  description?: string | null;
  category: DocumentCategory;
  subcategory?: DocumentSubcategory | null;
  documentType: DocumentType;
  status: DocumentStatus;
  ownerType: DocumentOwnerType;
  ownerID: string;
  currentVersionID?: string | null;
  tags: string[];
  issuedAt?: number | null;
  effectiveFrom?: number | null;
  effectiveTo?: number | null;
  expiresAt?: number | null;
  warrantyEndsAt?: number | null;
  visibilityScope: DocumentVisibilityScope;
  createdBy: string;
  updatedBy: string;
}

export interface DocumentVersion extends IAwsRecord {
  typeName: typeof TypeNames.DocumentVersion;
  vaultRecordType: typeof VaultRecordType.DocumentVersion;
  versionID: string;
  documentID: string;
  propertyID: string;
  storageKey: string;
  bucket: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256?: string | null;
  versionNumber: number;
  isLatest: boolean;
  uploadStatus: DocumentVersionUploadStatus;
  uploadedAt: number;
  uploadedBy: string;
  virusScanStatus: DocumentVersionVirusScanStatus;
  extractionStatus?: DocumentProcessingStatus | null;
  previewStatus?: DocumentProcessingStatus | null;
}

export interface DocumentLink extends IAwsRecord {
  typeName: typeof TypeNames.DocumentLink;
  vaultRecordType: typeof VaultRecordType.DocumentLink;
  linkID: string;
  documentID: string;
  propertyID: string;
  entityType: DocumentLinkedEntityType;
  entityID: string;
  relationType: DocumentRelationType;
  createdBy: string;
}

export interface DocumentAuditEvent extends IAwsRecord {
  typeName: typeof TypeNames.DocumentAuditEvent;
  vaultRecordType: typeof VaultRecordType.DocumentAuditEvent;
  auditEventID: string;
  documentID: string;
  propertyID: string;
  versionID?: string | null;
  action: DocumentAuditAction;
  actorID: string;
  actorType: VaultAuditActorType;
  timestamp: number;
  metadata?: VaultJsonObject | null;
}

export interface DocumentSubcategoryTaxonomyItem {
  subcategory: DocumentSubcategory;
  label: string;
  documentCount?: number;
}

export interface DocumentCategoryTaxonomyItem {
  category: DocumentCategory;
  label: string;
  documentCount?: number;
  subcategories: DocumentSubcategoryTaxonomyItem[];
}

export interface DocumentTaxonomyResponse {
  items: DocumentCategoryTaxonomyItem[];
  includeCounts: boolean;
}

export interface CreatePropertyDocumentRequest {
  title: string;
  description?: string | null;
  category?: DocumentCategory;
  subcategory?: DocumentSubcategory | null;
  documentType?: DocumentType;
  status?: DocumentStatus;
  orgID?: string | null;
  projectID?: string | null;
  ownerType?: DocumentOwnerType;
  ownerID?: string;
  tags?: string[];
  issuedAt?: number | null;
  effectiveFrom?: number | null;
  effectiveTo?: number | null;
  expiresAt?: number | null;
  warrantyEndsAt?: number | null;
  visibilityScope?: DocumentVisibilityScope;
}

export type UpdatePropertyDocumentRequest = Partial<CreatePropertyDocumentRequest>;

export interface CreateUploadIntentRequest {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface CompleteUploadRequest {
  versionID: string;
  versionNumber: number;
}

export interface CreateDocumentLinkRequest {
  entityType: DocumentLinkedEntityType;
  entityID: string;
  relationType: DocumentRelationType;
}

export interface UploadIntentData {
  version: DocumentVersion;
  uploadUrl: string;
  expiresIn: number;
}

export interface CreatedApiResponse<T> {
  message: string;
  data: T;
}

export interface CompleteUploadResponse {
  document: PropertyDocument;
  version: DocumentVersion;
}

export interface DownloadDocumentUrlResponse {
  downloadUrl: string;
  expiresIn: number;
  version: DocumentVersion;
}

export interface GetDocumentResponse {
  document: PropertyDocument;
  versions: DocumentVersion[];
  links: DocumentLink[];
}

export interface EntityDocumentLinkResponse {
  link: DocumentLink;
  document: PropertyDocument;
}

export interface DeletePropertyDocumentResponse {
  documentID: string;
  alreadyDeleted?: boolean;
  versionCount: number;
  linkCount: number;
  auditEventCount: number;
  storageObjectCount: number;
  storageDeleteFailures: string[];
}

export interface ListDocumentsFilters {
  category?: DocumentCategory;
  subcategory?: DocumentSubcategory;
  status?: DocumentStatus;
}
